import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import BulkForecastReview from '@/components/procurement/BulkForecastReview';
import InsightsPanel from '@/components/procurement/InsightsPanel';
import InlineEdit from '@/components/procurement/InlineEdit';
import {
  Loader2, Sparkles, Flame, AlertOctagon, AlertTriangle, ShieldAlert,
  Truck, RefreshCw, Package as PackageIcon, X, Calendar, Zap, ArrowUp, ArrowDown,
  Minus, FileSpreadsheet, Filter,
} from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)', surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)',
};

const STATUS_COL = {
  DONE: '#1E4E8C', ON_TRACK: '#22C55E', AT_RISK: '#F59E0B', DELAYED: '#EF4444', NOT_STARTED: '#CBD5E1',
};

const Delta = ({ v, invert }) => {
  if (v == null || v === 0) return <span className="inline-flex items-center text-[9px]" style={{ color: C.slateMuted }}><Minus className="w-2 h-2" />0</span>;
  const isBad = invert ? v < 0 : v > 0;
  const Icon = v > 0 ? ArrowUp : ArrowDown;
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-mono" style={{ color: isBad ? '#EF4444' : '#22C55E' }}>
      <Icon className="w-2 h-2" />{Math.abs(v)}
    </span>
  );
};

// The brand navy is too dark to read on the obsidian theme — labels use the
// theme-lifted tone while the card border keeps the hex (var() can't take an alpha suffix).
const TONE_TEXT = { '#1E4E8C': 'var(--tone-navy-text)' };

const KPI = ({ label, value, tone, icon: Icon, sub, delta, deltaInvert }) => (
  <div className="p-3 rounded-lg" style={{ background: C.surface, border: `1px solid ${tone}33` }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1 text-[10px] uppercase font-semibold tracking-[0.08em]" style={{ color: TONE_TEXT[tone] || tone }}>
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      {delta !== undefined && <Delta v={delta} invert={deltaInvert} />}
    </div>
    <div className="mt-1 text-2xl font-bold leading-none" style={{ color: C.slate, fontFamily: 'Inter' }}>{value}</div>
    {sub && <div className="mt-0.5 text-[10px]" style={{ color: C.slateMuted }}>{sub}</div>}
  </div>
);

function MiniTimeline({ upcoming }) {
  const today = new Date();
  const cells = useMemo(() => {
    const arr = Array.from({ length: 30 }, (_, i) => {
      const d = new Date(today); d.setDate(today.getDate() + i);
      return { date: d, items: [] };
    });
    upcoming.forEach(u => {
      const d = new Date(u.date);
      const diff = Math.floor((d - today) / (1000 * 60 * 60 * 24));
      if (diff >= 0 && diff < 30) arr[diff].items.push(u);
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcoming]);
  const max = Math.max(1, ...cells.map(c => c.items.length));
  return (
    <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-timeline">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1" style={{ color: C.primaryText }}>
          <Calendar className="w-3 h-3" /> Next 30 Days · Milestone Density
        </div>
        <div className="text-[10px]" style={{ color: C.slateMuted }}>{upcoming.length} upcoming gates</div>
      </div>
      <div className="flex items-end gap-[3px] h-16">
        {cells.map((c, i) => {
          const h = c.items.length ? (18 + (c.items.length / max) * 44) : 4;
          const hot = c.items.length >= max * 0.66;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end relative" style={{ height: '100%' }}>
              <div className="w-full rounded-t transition-all"
                style={{ height: h, background: c.items.length ? (hot ? C.accent : C.primary) : 'var(--border-color)',
                          opacity: c.items.length ? 0.9 : 0.5 }}
                title={`${c.date.toLocaleDateString()} · ${c.items.length} gate(s)`} />
              {i % 5 === 0 && (
                <div className="absolute -bottom-4 text-[8px] font-mono" style={{ color: C.slateMuted }}>
                  {c.date.getDate()}/{c.date.getMonth() + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 max-h-40 overflow-y-auto space-y-1">
        {upcoming.slice(0, 10).map((u, i) => (
          <div key={i} className="flex items-center justify-between text-[11px] py-1" style={{ borderBottom: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[10px] font-bold" style={{ color: C.primaryText }}>{u.mciLineNumber}</span>
              <span className="truncate" style={{ color: C.slate, maxWidth: 320 }}>{u.materialDescription}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px]" style={{ color: C.slateMuted }}>
              <span className="px-1.5 py-0.5 rounded" style={{ background: `${C.accent}14`, color: C.accent }}>{u.gate}</span>
              <span className="font-mono">{new Date(u.date).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
        {upcoming.length === 0 && <div className="text-[11px] text-center py-3" style={{ color: C.slateMuted }}>No gates due in the next 30 days.</div>}
      </div>
    </div>
  );
}

function LeadTimeTrend({ projectId }) {
  const [pts, setPts] = useState([]);
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/mci/control-tower/pre-order/trend?weeks=12`)
      .then(r => setPts(r.data.trend || [])).catch(() => setPts([]));
  }, [projectId]);
  if (pts.length < 2) return null;
  const values = pts.map(p => p.kpis?.weightedLeadTimeDays || 0);
  const max = Math.max(1, ...values);
  const w = 260, h = 40;
  const pathD = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i / (values.length - 1)) * w} ${h - (v / max) * h}`).join(' ');
  return (
    <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-trend">
      <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-1" style={{ color: C.primaryText }}>
        Lead Time Trend (12 wk)
      </div>
      <svg width={w} height={h} className="block">
        <path d={pathD} fill="none" stroke={C.accent} strokeWidth={2} />
      </svg>
      <div className="flex justify-between text-[9px]" style={{ color: C.slateMuted }}>
        <span>{pts[0]?.week}</span><span className="font-bold" style={{ color: C.accent }}>{values[values.length - 1]}d</span>
        <span>{pts[pts.length - 1]?.week}</span>
      </div>
    </div>
  );
}

function ExpediteDialog({ projectId, pkg, onClose, onDone }) {
  const [form, setForm] = useState({ reason: '', dueDays: 7, priority: 'HIGH', owner: pkg.vendor || '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/actions/expedite`,
        { mciLineNumber: pkg.mciLineNumber, ...form });
      toast.success(`Expedite raised · ${r.data.raid.raidNumber}`);
      onDone?.(r.data.raid); onClose();
    } catch (e) { toast.error('Failed to raise expedite'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="expedite-dialog">
      <div className="w-full max-w-md p-5 rounded-lg" onClick={e => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: C.accent }}>Raise Expedite Action</div>
            <div className="font-mono text-sm font-bold" style={{ color: C.primaryText }}>{pkg.mciLineNumber}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-semibold" style={{ color: C.slateMuted }}>REASON</label>
            <textarea rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
              placeholder="Vendor delay, awaiting drawings, customs hold…"
              className="w-full mt-1 p-2 rounded text-xs border" style={{ borderColor: C.border, background: C.surfaceAlt }}
              data-testid="expedite-reason" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] font-semibold" style={{ color: C.slateMuted }}>PRIORITY</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full mt-1 p-1.5 rounded text-xs border" style={{ borderColor: C.border, background: C.surfaceAlt }}
                data-testid="expedite-priority">
                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold" style={{ color: C.slateMuted }}>DUE (days)</label>
              <input type="number" value={form.dueDays} onChange={e => setForm({ ...form, dueDays: +e.target.value })}
                className="w-full mt-1 p-1.5 rounded text-xs border" style={{ borderColor: C.border, background: C.surfaceAlt }} />
            </div>
            <div>
              <label className="text-[10px] font-semibold" style={{ color: C.slateMuted }}>OWNER</label>
              <input value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })}
                className="w-full mt-1 p-1.5 rounded text-xs border" style={{ borderColor: C.border, background: C.surfaceAlt }} />
            </div>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full text-white h-8 text-xs" style={{ background: C.accent }}
            data-testid="expedite-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />} Raise Expedite (writes to RAID)
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ControlTowerPreOrder({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [expedite, setExpedite] = useState(null);
  const [filters, setFilters] = useState({ discipline: '', vendor: '', priority: '', longLead: '', search: '', materialCategory: 'ALL' });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const debounceRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== null) params.append(k, v); });
      const { data: d } = await api.get(`/v1/projects/${projectId}/mci/control-tower/pre-order?${params}`);
      setData(d);
    } catch (e) { toast.error('Failed to load Pre-Order Control Tower'); }
    setLoading(false);
  };

  const openRegisterBulk = async () => {
    setBulkBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.materialCategory && filters.materialCategory !== 'ALL') params.append('materialCategory', filters.materialCategory);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.discipline) params.append('discipline', filters.discipline);
      const { data: r } = await api.post(`/v1/projects/${projectId}/mci/control-tower/gate-forecast/register-cognara-suggest?${params}`);
      if (!r.count) return toast.info('Nothing to forecast — no open gates in current filter.');
      setBulkRows(r.rows || []);
      setBulkOpen(true);
    } catch (e) { toast.error('Cognara register-wide suggest failed'); }
    setBulkBusy(false);
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/v1/projects/${projectId}/mci/control-tower/export/xlsx?phase=PRE`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'pre-order-control-tower.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const [threshold, setThreshold] = useState(90);
  const [adminBusy, setAdminBusy] = useState(false);
  const migrateCanonical = async () => {
    setAdminBusy(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/migrate-canonical-gates`);
      toast.success(`Migrated ${r.data.configsUpdated} configs · ${r.data.entriesUpdated} entries to 7/10 canonical gates`);
      load();
    } catch (e) { toast.error('Migration failed'); }
    setAdminBusy(false);
  };
  const backfillWbs = async () => {
    setAdminBusy(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/backfill-wbs-links`);
      toast.success(`${r.data.linked} MCIs linked to construction WBS`);
      load();
    } catch (e) { toast.error('Backfill failed'); }
    setAdminBusy(false);
  };
  const seedHistory = async () => {
    setAdminBusy(true);
    try {
      const r = await api.post(`/v1/projects/${projectId}/mci/control-tower/seed-historical-snapshots`);
      toast.success(`Seeded ${r.data.seeded} historical snapshots · trend now populated`);
      load();
    } catch (e) { toast.error('Seed failed'); }
    setAdminBusy(false);
  };
  const saveThreshold = async () => {
    try {
      const r = await api.put(`/v1/projects/${projectId}/mci/control-tower/settings/long-lead`, { longLeadDays: threshold });
      toast.success(`Threshold saved · ${r.data.flagged} MCIs flagged as long-lead`);
      load();
    } catch (e) { toast.error('Save failed'); }
  };
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/mci/control-tower/settings`).then(r => setThreshold(r.data.longLeadDays || 90)).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [projectId, filters]);

  useEffect(() => {
    // Listen for register-edit events to refresh
    const h = () => load();
    window.addEventListener('mci-register-updated', h);
    return () => window.removeEventListener('mci-register-updated', h);
    // eslint-disable-next-line
  }, [projectId]);

  const gateNames = useMemo(() => data?.canonicalGates || [], [data]);

  if (loading && !data) return (
    <div className="p-12 text-center text-sm" style={{ color: C.slateMuted }}>
      <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" style={{ color: C.primaryText }} />
      Loading Pre-Order Control Tower…
    </div>
  );
  if (!data) return null;
  const k = data.kpis; const dd = data.deltas || {};

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="ct-preorder" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="col-span-12 lg:col-span-9 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <KPI label="Total Packages" value={k.totalPackages} tone={C.primary} icon={PackageIcon} delta={dd.totalPackages} />
          <KPI label="On-Track" value={k.onTrack} tone="#22C55E" icon={ShieldAlert} delta={dd.onTrack} />
          <KPI label="Delayed" value={k.delayed} tone="#EF4444" icon={Flame} delta={dd.delayed} deltaInvert />
          <KPI label="Critical Path" value={k.criticalPathImpact} tone={C.accent} icon={AlertOctagon} delta={dd.criticalPathImpact} deltaInvert />
          <KPI label="Long-Lead Watch" value={k.longLeadWatch} tone="#F59E0B" icon={AlertTriangle} delta={dd.longLeadWatch} deltaInvert />
          <KPI label="Lead Time (d)" value={k.weightedLeadTimeDays} tone={C.primary} icon={RefreshCw} sub="MR → PO Award" delta={dd.weightedLeadTimeDays} deltaInvert />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }} data-testid="ct-filters">
          <Filter className="w-3 h-3" style={{ color: C.primaryText }} />
          <div className="flex rounded overflow-hidden" style={{ border: `1px solid ${C.border}` }} data-testid="ct-cat-filter">
            {['ALL', 'BULK', 'TAGGED'].map(x => (
              <button key={x} onClick={() => setFilters({ ...filters, materialCategory: x })}
                className="px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: filters.materialCategory === x ? C.primaryText : C.surface,
                          color: filters.materialCategory === x ? '#fff' : C.slateMuted }}
                data-testid={`ct-cat-${x.toLowerCase()}`}>{x}</button>
            ))}
          </div>
          <input placeholder="Search MCI/desc/vendor…" value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface, width: 180 }}
            data-testid="ct-search" />
          <select value={filters.discipline} onChange={e => setFilters({ ...filters, discipline: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface }}
            data-testid="ct-filter-discipline">
            <option value="">All Disciplines</option>
            {(data.facets?.disciplines || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.vendor} onChange={e => setFilters({ ...filters, vendor: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface }}>
            <option value="">All Vendors</option>
            {(data.facets?.vendors || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface }}>
            <option value="">All Priorities</option>
            {['CRITICAL', 'HIGH', 'NORMAL', 'LOW'].map(d => <option key={d}>{d}</option>)}
          </select>
          <select value={filters.longLead} onChange={e => setFilters({ ...filters, longLead: e.target.value })}
            className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: C.border, background: C.surface }}>
            <option value="">All Items</option>
            <option value="true">Long-Lead Only</option>
            <option value="false">Non-Long-Lead</option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px]" style={{ color: C.slateMuted }}>{data.packages.length} shown</span>
            <div className="flex items-center gap-1 text-[10px]" style={{ color: C.slateMuted }}>
              Long-lead ≥
              <input type="number" value={threshold} onChange={e => setThreshold(+e.target.value)}
                className="w-14 px-1 py-0.5 rounded border text-[10px] font-mono" style={{ borderColor: C.border, background: C.surface }}
                data-testid="ll-threshold" /> d
              <button onClick={saveThreshold} className="px-1.5 py-0.5 text-[10px] rounded" style={{ background: C.primary, color: '#fff' }}
                data-testid="ll-save">Save</button>
            </div>
            <PermissionGate module="MCI" action="configure">
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={migrateCanonical} disabled={adminBusy} className="h-6 text-[10px]" data-testid="migrate-gates-btn">Migrate 7/10 Gates</Button>
                <Button size="sm" variant="outline" onClick={backfillWbs} disabled={adminBusy} className="h-6 text-[10px]" data-testid="backfill-wbs-btn">Backfill WBS</Button>
                <Button size="sm" variant="outline" onClick={seedHistory} disabled={adminBusy} className="h-6 text-[10px]" data-testid="seed-history-btn">Seed Trend</Button>
              </div>
            </PermissionGate>
            <PermissionGate module="MCI" action="export">
              <Button size="sm" variant="outline" onClick={exportXlsx} className="h-6 text-[10px]" data-testid="ct-export-btn">
                <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
              </Button>
            </PermissionGate>
            <Button size="sm" variant="ghost" onClick={load} className="h-6 text-[10px]"><RefreshCw className="w-3 h-3" /></Button>
          </div>
        </div>

        <div className="rounded-lg p-3 overflow-auto" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-heatmap">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: C.primaryText }}>
              Package × 7 Canonical Pre-Order Gates
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              Showing {Math.min(60, data.packages.length)} of {data.packages.length}
            </div>
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ background: `${C.primary}08` }}>
                <th className="p-2 text-left w-52" style={{ color: C.slate }}>Package</th>
                <th className="p-2 text-left w-24" style={{ color: C.slate }}>Vendor</th>
                {gateNames.map((g, i) => (
                  <th key={i} className="p-2 text-center text-[9px] font-semibold uppercase" title={g} style={{ color: C.slateMuted }}>
                    {g?.slice(0, 10) || `G${i + 1}`}
                  </th>
                ))}
                <th className="p-2 text-center" style={{ color: C.slate }}>Prio</th>
              </tr>
            </thead>
            <tbody>
              {data.packages.length === 0 && (
                <tr><td colSpan={gateNames.length + 3} className="p-6 text-center" style={{ color: C.slateMuted }}>No packages match filters.</td></tr>
              )}
              {data.packages.slice(0, 60).map(p => (
                <tr key={p.mciLineNumber} className="cursor-pointer hover:bg-blue-50/40"
                  onClick={() => setSelected(p.mciLineNumber)} data-testid={`ct-pkg-${p.mciLineNumber}`}
                  title={`${p.mciLineNumber} · ${p.materialDescription}\nVendor: ${p.vendor} · Priority: ${p.priority}${p.isLongLead ? ' · LONG-LEAD' : ''}\nClick to open package drilldown`}
                  style={{ borderTop: `1px solid ${C.border}` }}>
                  <td className="p-1.5">
                    <div className="font-mono text-[10px] font-bold" style={{ color: C.primaryText }}>{p.mciLineNumber}</div>
                    <div className="text-[10px] truncate" style={{ color: C.slateMuted, maxWidth: 260 }}>{p.materialDescription}</div>
                  </td>
                  <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }} onClick={e => e.stopPropagation()}>
                    <InlineEdit value={p.vendor} projectId={projectId} mci={p.mciLineNumber}
                      field="vendor" onSaved={load} testIdPrefix="vendor" />
                  </td>
                  {gateNames.map((gn, i) => {
                    const g = p.gates.find(x => x.name === gn);
                    if (!g) return <td key={i} className="p-1" title={`${gn}: not in this material's workflow`}><div className="w-6 h-6 mx-auto rounded" style={{ background: 'var(--card-bg-alt)' }} /></td>;
                    const tip = [
                      `${p.mciLineNumber} — ${gn}`,
                      `Status: ${g.status.replace('_', ' ')}`,
                      `Planned: ${g.planned?.slice(0, 10) || '—'}`,
                      `Forecast: ${g.forecast?.slice(0, 10) || '—'}`,
                      `Actual: ${g.actual?.slice(0, 10) || '—'}`,
                      g.remarks ? `Remarks: ${g.remarks}` : '',
                      'Legend: BLUE=Done · GREEN=On-Track · AMBER=At-Risk · RED=Delayed · GREY=Not-Started',
                    ].filter(Boolean).join('\n');
                    return (
                      <td key={i} className="p-1 text-center" title={tip}>
                        <div className="w-6 h-6 mx-auto rounded" style={{ background: STATUS_COL[g.status] || '#CBD5E1', opacity: g.status === 'NOT_STARTED' ? 0.35 : 1 }} />
                      </td>
                    );
                  })}
                  <td className="p-1.5 text-center text-[9px] font-bold" onClick={e => e.stopPropagation()}>
                    <InlineEdit value={p.priority} projectId={projectId} mci={p.mciLineNumber}
                      field="priority" type="select"
                      options={['CRITICAL','HIGH','NORMAL','LOW']}
                      onSaved={load} testIdPrefix="priority"
                      style={{ color: p.priority === 'CRITICAL' ? C.accent : C.slateMuted }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 rounded-lg p-2" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }} data-testid="ct-legend">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-1" style={{ color: C.primaryText }}>Legend</div>
            <div className="flex items-center gap-3 text-[10px] flex-wrap" style={{ color: C.slateMuted }}>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm" style={{ background: '#1E4E8C' }} /><b>DONE</b> — actual on/before planned</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm" style={{ background: '#22C55E' }} /><b>ON-TRACK</b> — forecast ≤ planned + 7d</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm" style={{ background: '#F59E0B' }} /><b>AT-RISK</b> — planned passed (actual missing) or slip 7–14d</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm" style={{ background: '#EF4444' }} /><b>DELAYED</b> — actual after planned, or forecast slipped &gt;14d past planned</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm" style={{ background: '#CBD5E1', opacity: 0.5 }} /><b>NOT STARTED</b></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm" style={{ background: 'var(--card-bg-alt)' }} /><b>N/A</b> — gate not in this material's workflow (BULK skips CBE/PO Recommend/Approve)</div>
            </div>
            <div className="text-[10px] mt-1" style={{ color: C.slateMuted }}>Hover any cell for full planned/forecast/actual dates. Click any row to open package drilldown with PO, vendor drawings, and per-gate documents.</div>
          </div>
        </div>

        <MiniTimeline upcoming={data.upcoming} />
      </div>

      <div className="col-span-12 lg:col-span-3 space-y-3">
        <LeadTimeTrend projectId={projectId} />
        <div className="rounded-lg p-3" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="ct-critical">
          <div className="text-[10px] uppercase tracking-[0.08em] font-semibold mb-2 flex items-center gap-1" style={{ color: '#EF4444' }}>
            <Flame className="w-3 h-3" /> Critical Orders · {data.criticalOrders.length}
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {data.criticalOrders.length === 0 ? (
              <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>All clear.</div>
            ) : data.criticalOrders.map(p => (
              <div key={p.mciLineNumber} className="p-2 rounded text-[10px]" style={{ background: '#EF44440d', border: '1px solid #EF444444' }}>
                <div className="font-mono font-bold text-[11px]" style={{ color: 'var(--tone-red-text)' }}>{p.mciLineNumber}</div>
                <div className="truncate" style={{ color: C.slate }}>{p.materialDescription}</div>
                <div style={{ color: C.slateMuted }}>{p.vendor} · {p.priority}</div>
                <PermissionGate module="MCI" action="update">
                  <button className="mt-1 px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: C.accent, color: '#fff' }}
                    onClick={() => setExpedite(p)} data-testid={`expedite-${p.mciLineNumber}`}>
                    <Zap className="w-3 h-3 inline mr-0.5" /> Expedite
                  </button>
                </PermissionGate>
              </div>
            ))}
          </div>
        </div>

        <InsightsPanel projectId={projectId} phase="PRE_ORDER" color={C.primary} testId="ct-insights" />
      </div>

      {selected && <MciPackageDrawer projectId={projectId} mci={selected} onClose={() => { setSelected(null); load(); }} />}

      {expedite && <ExpediteDialog projectId={projectId} pkg={expedite} onClose={() => setExpedite(null)} onDone={() => load()} />}

      {bulkOpen && (
        <BulkForecastReview projectId={projectId} rows={bulkRows}
          title={`Cognara Forecast Register · ${filters.materialCategory}${filters.priority ? ' · ' + filters.priority : ''}`}
          onClose={() => setBulkOpen(false)} onApplied={() => load()} />
      )}
    </div>
  );
}
